/**
 * DischargeSummaryPrint — A4 patient discharge summary document.
 * Route: /discharge/print/:id
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { PrintShell, PrintField, PrintSection, PrintSignatures, fmtDate } from '../components/print/PrintShell';

const DischargeSummaryPrint = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/discharge/${id}`)
      .then(res => { if (mounted) setRecord(res.data); })
      .catch(async () => {
        // Fallback: scan the "patient/all" endpoint
        try {
          const all = await api.get('/discharge/patient/all');
          const found = (all.data || []).find(r => r.id === id);
          if (mounted) setRecord(found || null);
        } catch { if (mounted) setRecord(null); }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  const r = record || {};
  const status = r.status || 'finalized';
  const statusColor = status === 'finalized' ? '#10b981' : status === 'draft' ? '#f59e0b' : '#64748b';

  return (
    <PrintShell
      title="Discharge Summary"
      subtitle="Final clinical summary upon hospital discharge."
      loading={loading}
      notFound={!loading && !record}
      hospitalId={r.hospital_id}
      docRef={`DIS-${(r.id || '').slice(0, 8).toUpperCase()}`}
      statusPill={{ label: status, color: statusColor }}>
      <PrintSection title="Patient Details">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <PrintField label="Patient Name" value={r.patient_name} />
          <PrintField label="UHID" value={r.patient_uhid} mono />
          <PrintField label="Age / Gender" value={[r.age, r.gender].filter(Boolean).join(' / ')} />
          <PrintField label="Phone" value={r.patient_phone} mono />
        </div>
      </PrintSection>

      <PrintSection title="Admission Details">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <PrintField label="Admission Date" value={fmtDate(r.admission_date)} />
          <PrintField label="Discharge Date" value={fmtDate(r.discharge_date || r.created_at)} />
          <PrintField label="Ward / Bed" value={[r.ward_name, r.bed_number].filter(Boolean).join(' · ')} />
          <PrintField label="Attending Physician" value={r.attending_physician || r.doctor_name} />
        </div>
      </PrintSection>

      {(r.diagnosis || r.final_diagnosis) && (
        <PrintSection title="Final Diagnosis">
          <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed">{r.diagnosis || r.final_diagnosis}</p>
        </PrintSection>
      )}

      {r.chief_complaints && (
        <PrintSection title="Chief Complaints / History">
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{r.chief_complaints}</p>
        </PrintSection>
      )}

      {(r.investigations || r.lab_summary) && (
        <PrintSection title="Investigations Summary">
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{r.investigations || r.lab_summary}</p>
        </PrintSection>
      )}

      {(r.treatment_given || r.treatment) && (
        <PrintSection title="Treatment Given">
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{r.treatment_given || r.treatment}</p>
        </PrintSection>
      )}

      {(r.condition_at_discharge || r.discharge_condition) && (
        <PrintSection title="Condition at Discharge">
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{r.condition_at_discharge || r.discharge_condition}</p>
        </PrintSection>
      )}

      {(r.follow_up_instructions || r.follow_up) && (
        <PrintSection title="Follow-Up Instructions">
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{r.follow_up_instructions || r.follow_up}</p>
        </PrintSection>
      )}

      {Array.isArray(r.medications) && r.medications.length > 0 && (
        <PrintSection title="Medications on Discharge">
          <table className="w-full text-xs border border-slate-200 rounded">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-2 py-1.5 text-left">Drug</th>
                <th className="px-2 py-1.5 text-left">Dosage</th>
                <th className="px-2 py-1.5 text-left">Frequency</th>
                <th className="px-2 py-1.5 text-left">Duration</th>
              </tr>
            </thead>
            <tbody>
              {r.medications.map((m, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{m.name || m.drug_name}</td>
                  <td className="px-2 py-1.5">{m.dosage}</td>
                  <td className="px-2 py-1.5">{m.frequency}</td>
                  <td className="px-2 py-1.5">{m.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PrintSection>
      )}

      <PrintSignatures
        left="Attending Physician"
        leftLabel={r.attending_physician || r.doctor_name || '—'}
        right="Patient / Guardian"
        rightLabel={r.patient_name || '—'}
      />
    </PrintShell>
  );
};

export default DischargeSummaryPrint;
